import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { appointmentsAPI, servicesAPI, barbersAPI, clientsAPI } from '../lib/api';
import { formatDate, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar,
  Clock,
  Gift,
  History,
  Plus,
  Star,
  User,
  Loader2,
  CheckCircle,
  XCircle,
  Edit,
  Scissors
} from 'lucide-react';
import { toast } from 'sonner';

export default function ClientePanelPage() {
  const { user, selectedBusiness } = useAuth();
  const [clientProfile, setClientProfile] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  const [bookingData, setBookingData] = useState({
    service_ids: [],
    barber_id: '',
    date: '',
    start_time: '',
    notes: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedBusiness || !user) return;
    
    try {
      setLoading(true);
      
      // Get client profile by email
      const clientsRes = await clientsAPI.getAll({ search: user.email });
      const myProfile = clientsRes.data.find(c => c.email === user.email);
      setClientProfile(myProfile);
      
      // Get services and barbers
      const [servicesRes, barbersRes] = await Promise.all([
        servicesAPI.getAll(),
        barbersAPI.getAll()
      ]);
      
      setServices(servicesRes.data || []);
      setBarbers(barbersRes.data || []);
      
      // Get appointments for this client
      if (myProfile) {
        const appointmentsRes = await appointmentsAPI.getAll({ client_id: myProfile.id });
        setAppointments(appointmentsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading client data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const calculateEndTime = (startTime, serviceIds) => {
    if (!startTime || serviceIds.length === 0) return '';
    const totalMinutes = serviceIds.reduce((acc, sid) => {
      const service = services.find(s => s.id === sid);
      return acc + (service?.duration_minutes || 30);
    }, 0);
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const endDate = new Date(2000, 0, 1, hours, minutes + totalMinutes);
    return `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`;
  };

  const handleBookAppointment = async () => {
    if (!bookingData.service_ids.length || !bookingData.barber_id || !bookingData.date || !bookingData.start_time) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }
    
    if (!clientProfile) {
      toast.error('No se encontró tu perfil de cliente');
      return;
    }

    try {
      const endTime = calculateEndTime(bookingData.start_time, bookingData.service_ids);
      
      await appointmentsAPI.create({
        client_id: clientProfile.id,
        barber_id: bookingData.barber_id,
        service_ids: bookingData.service_ids,
        date: bookingData.date,
        start_time: bookingData.start_time,
        end_time: endTime,
        notes: bookingData.notes
      });
      
      toast.success('Cita agendada exitosamente');
      setBookingDialogOpen(false);
      setBookingData({ service_ids: [], barber_id: '', date: '', start_time: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar cita');
    }
  };

  const handleReschedule = async () => {
    if (!selectedAppointment || !bookingData.date || !bookingData.start_time) {
      toast.error('Por favor completa la nueva fecha y hora');
      return;
    }

    try {
      const endTime = calculateEndTime(bookingData.start_time, selectedAppointment.service_ids || []);
      
      await appointmentsAPI.update(selectedAppointment.id, {
        date: bookingData.date,
        start_time: bookingData.start_time,
        end_time: endTime
      });
      
      toast.success('Cita reagendada exitosamente');
      setEditDialogOpen(false);
      setSelectedAppointment(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reagendar cita');
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;

    try {
      await appointmentsAPI.delete(selectedAppointment.id);
      toast.success('Cita cancelada exitosamente');
      setCancelDialogOpen(false);
      setSelectedAppointment(null);
      loadData();
    } catch (error) {
      toast.error('Error al cancelar cita');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pendiente', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      confirmed: { label: 'Confirmada', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
      in_progress: { label: 'En progreso', className: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
      completed: { label: 'Completada', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
      cancelled: { label: 'Cancelada', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
      no_show: { label: 'No asistió', className: 'bg-slate-100 text-slate-500 border-slate-300' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getLoyaltyLevel = (points) => {
    if (points >= 500) return { name: 'Platino', color: 'text-purple-400', icon: Star };
    if (points >= 200) return { name: 'Oro', color: 'text-yellow-400', icon: Star };
    if (points >= 50) return { name: 'Plata', color: 'text-slate-500', icon: Star };
    return { name: 'Bronce', color: 'text-orange-400', icon: Star };
  };

  // Generate available time slots
  const generateTimeSlots = () => {
    const slots = [];
    for (let h = 9; h < 20; h++) {
      slots.push(`${String(h).padStart(2, '0')}:00`);
      slots.push(`${String(h).padStart(2, '0')}:30`);
    }
    return slots;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-500" />
      </div>
    );
  }

  const upcomingAppointments = appointments.filter(a => 
    ['pending', 'confirmed'].includes(a.status) && a.date >= new Date().toISOString().split('T')[0]
  ).sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
  
  const pastAppointments = appointments.filter(a => 
    ['completed', 'cancelled', 'no_show'].includes(a.status) || a.date < new Date().toISOString().split('T')[0]
  ).sort((a, b) => b.date.localeCompare(a.date));

  const loyaltyLevel = getLoyaltyLevel(clientProfile?.loyalty_points || 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cliente-panel-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            Mi Cuenta
          </h1>
          <p className="text-slate-500 mt-1">
            Bienvenido, {clientProfile?.first_name || user?.first_name || 'Cliente'}
          </p>
        </div>
        <Button 
          onClick={() => setBookingDialogOpen(true)}
          className="bg-primary text-white hover:bg-primary/90"
          data-testid="new-appointment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva cita
        </Button>
      </div>

      {/* Loyalty Card */}
      {clientProfile && (
        <Card className="border-slate-200 bg-gradient-to-br from-zinc-900 to-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-slate-100">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Puntos de lealtad</p>
                  <p className="text-3xl font-bold">{clientProfile.loyalty_points || 0}</p>
                </div>
              </div>
              <div className="text-right">
                <div className={cn("flex items-center gap-2", loyaltyLevel.color)}>
                  <loyaltyLevel.icon className="w-5 h-5" />
                  <span className="font-semibold">Nivel {loyaltyLevel.name}</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {clientProfile.total_visits || 0} visitas totales
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="upcoming" className="space-y-4">
        <TabsList className="bg-slate-50 border border-slate-200">
          <TabsTrigger value="upcoming" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <Calendar className="w-4 h-4 mr-2" />
            Próximas citas
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white data-[state=active]:text-black">
            <History className="w-4 h-4 mr-2" />
            Historial
          </TabsTrigger>
        </TabsList>

        {/* Upcoming Appointments */}
        <TabsContent value="upcoming" className="space-y-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="border-b border-slate-200">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Citas programadas
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {upcomingAppointments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <Calendar className="w-12 h-12 mb-3 opacity-50" />
                  <p>No tienes citas programadas</p>
                  <Button 
                    variant="link" 
                    onClick={() => setBookingDialogOpen(true)} 
                    className="mt-2"
                  >
                    Agendar una cita
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-zinc-800">
                    {upcomingAppointments.map((appointment, index) => (
                      <div 
                        key={appointment.id}
                        className="p-4 animate-fade-in"
                        style={{ animationDelay: `${index * 0.05}s` }}
                        data-testid={`upcoming-appointment-${appointment.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[80px] p-2 rounded-lg bg-slate-100">
                              <p className="text-xs text-slate-500">{formatDate(appointment.date)}</p>
                              <p className="text-lg font-bold">{appointment.start_time}</p>
                            </div>
                            <div>
                              <p className="font-medium">{appointment.service_names?.join(', ') || 'Servicio'}</p>
                              <p className="text-sm text-slate-500">
                                con {appointment.barber_name || 'Profesional'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {getStatusBadge(appointment.status)}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 hover:bg-slate-100"
                              onClick={() => {
                                setSelectedAppointment(appointment);
                                setBookingData({
                                  ...bookingData,
                                  date: appointment.date,
                                  start_time: appointment.start_time
                                });
                                setEditDialogOpen(true);
                              }}
                              title="Reagendar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="h-8 w-8 hover:bg-red-500/20 hover:text-red-400"
                              onClick={() => {
                                setSelectedAppointment(appointment);
                                setCancelDialogOpen(true);
                              }}
                              title="Cancelar"
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card className="border-slate-200 bg-white">
            <CardHeader className="border-b border-slate-200">
              <CardTitle className="text-lg" style={{ fontFamily: 'Manrope' }}>
                Historial de servicios
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pastAppointments.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                  <History className="w-12 h-12 mb-3 opacity-50" />
                  <p>No tienes historial de servicios</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="divide-y divide-zinc-800">
                    {pastAppointments.map((appointment, index) => (
                      <div 
                        key={appointment.id}
                        className="p-4 animate-fade-in"
                        style={{ animationDelay: `${index * 0.03}s` }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="text-center min-w-[80px]">
                              <p className="text-sm text-slate-500">{formatDate(appointment.date)}</p>
                              <p className="text-xs text-slate-600">{appointment.start_time}</p>
                            </div>
                            <div>
                              <p className="font-medium">{appointment.service_names?.join(', ') || 'Servicio'}</p>
                              <p className="text-sm text-slate-500">
                                con {appointment.barber_name || 'Profesional'}
                              </p>
                            </div>
                          </div>
                          {getStatusBadge(appointment.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Booking Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Agendar nueva cita</DialogTitle>
            <DialogDescription>Selecciona el servicio, profesional, fecha y hora.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Servicios *</Label>
              <Select 
                value={bookingData.service_ids[0] || ''} 
                onValueChange={(value) => setBookingData({ ...bookingData, service_ids: [value] })}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200">
                  <SelectValue placeholder="Seleccionar servicio" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <ScrollArea className="h-48">
                    {services.map(service => (
                      <SelectItem key={service.id} value={service.id}>
                        <div className="flex justify-between items-center w-full">
                          <span>{service.name}</span>
                          <span className="text-slate-500 ml-4">${service.price}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </ScrollArea>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Profesional *</Label>
              <Select 
                value={bookingData.barber_id} 
                onValueChange={(value) => setBookingData({ ...bookingData, barber_id: value })}
              >
                <SelectTrigger className="bg-slate-100 border-slate-200">
                  <SelectValue placeholder="Seleccionar profesional" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {barbers.map(barber => (
                    <SelectItem key={barber.id} value={barber.id}>
                      {barber.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={bookingData.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Hora *</Label>
                <Select 
                  value={bookingData.start_time} 
                  onValueChange={(value) => setBookingData({ ...bookingData, start_time: value })}
                >
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue placeholder="Hora" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <ScrollArea className="h-48">
                      {generateTimeSlots().map(slot => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={bookingData.notes}
                onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                className="bg-slate-100 border-slate-200 resize-none"
                placeholder="Alguna preferencia o comentario..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleBookAppointment} className="bg-primary text-white hover:bg-primary/90">
              Agendar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reschedule Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Reagendar cita</DialogTitle>
            <DialogDescription>Selecciona la nueva fecha y hora para tu cita.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nueva fecha *</Label>
                <Input
                  type="date"
                  value={bookingData.date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                  className="bg-slate-100 border-slate-200"
                />
              </div>
              <div className="space-y-2">
                <Label>Nueva hora *</Label>
                <Select 
                  value={bookingData.start_time} 
                  onValueChange={(value) => setBookingData({ ...bookingData, start_time: value })}
                >
                  <SelectTrigger className="bg-slate-100 border-slate-200">
                    <SelectValue placeholder="Hora" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <ScrollArea className="h-48">
                      {generateTimeSlots().map(slot => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </ScrollArea>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-200">
              Cancelar
            </Button>
            <Button onClick={handleReschedule} className="bg-primary text-white hover:bg-primary/90">
              Reagendar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Cancelar cita</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas cancelar esta cita? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>

          {selectedAppointment && (
            <div className="py-4">
              <div className="p-4 rounded-lg bg-slate-50">
                <p className="font-medium">{selectedAppointment.service_names?.join(', ')}</p>
                <p className="text-sm text-slate-500">
                  {formatDate(selectedAppointment.date)} a las {selectedAppointment.start_time}
                </p>
                <p className="text-sm text-slate-500">
                  con {selectedAppointment.barber_name}
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} className="border-slate-200">
              No, mantener cita
            </Button>
            <Button 
              onClick={handleCancelAppointment} 
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Sí, cancelar cita
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
